import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConsentStore } from '../stores/consentStore';

/**
 * GDPR / ePrivacy compliant cookie consent banner.
 *
 * - Shown on first visit when no consent decision has been recorded.
 * - No pre-checked boxes, no dark patterns.
 * - Clear accept / reject with equal visual weight.
 * - Customizable per-category toggles.
 */
export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const { consentGiven, acceptAll, acceptEssentialOnly, updatePreferences } =
    useConsentStore();

  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [advertisingChecked, setAdvertisingChecked] = useState(false);

  // Do not render if user has already made a choice
  if (consentGiven) return null;

  function handleSavePreferences() {
    updatePreferences({
      analytics: analyticsChecked,
      advertising: advertisingChecked,
    });
  }

  return (
    <div
      role="dialog"
      aria-label={t('components.cookieBanner.ariaLabel')}
      aria-modal="false"
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
    >
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200/70 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-card-dark">
        <div className="space-y-4">
          {/* Header text */}
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
              {t('components.cookieBanner.title')}
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {t('components.cookieBanner.description')}{' '}
              <Link
                to="/privacy"
                className="font-medium text-primary hover:underline"
              >
                {t('components.cookieBanner.privacyLink')}
              </Link>
            </p>
          </div>

          {/* Customize panel */}
          {showCustomize && (
            <div className="space-y-3 rounded-xl border border-slate-200/70 bg-slate-50/60 p-4 dark:border-white/10 dark:bg-white/5">
              {/* Essential — always on, not toggleable */}
              <label className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('components.cookieBanner.essentialLabel')}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('components.cookieBanner.essentialDesc')}
                  </p>
                </div>
                <span className="text-xs font-medium text-slate-400 dark:text-slate-500 select-none">
                  {t('components.cookieBanner.essentialAlwaysOn')}
                </span>
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('components.cookieBanner.analyticsLabel')}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('components.cookieBanner.analyticsDesc')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analyticsChecked}
                  onChange={(e) => setAnalyticsChecked(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 accent-blue-600 focus:ring-blue-600"
                />
              </label>

              {/* Advertising */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">
                    {t('components.cookieBanner.advertisingLabel')}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('components.cookieBanner.advertisingDesc')}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={advertisingChecked}
                  onChange={(e) => setAdvertisingChecked(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 accent-blue-600 focus:ring-blue-600"
                />
              </label>
            </div>
          )}

          {/* Action buttons — equal visual weight for accept and reject */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            {!showCustomize ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowCustomize(true)}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors"
                >
                  {t('components.cookieBanner.customize')}
                </button>
                <button
                  type="button"
                  onClick={acceptEssentialOnly}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors"
                >
                  {t('components.cookieBanner.essentialOnly')}
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] hover:bg-blue-700 transition-colors"
                >
                  {t('components.cookieBanner.acceptAll')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowCustomize(false)}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors"
                >
                  {t('components.cookieBanner.back')}
                </button>
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] hover:bg-blue-700 transition-colors"
                >
                  {t('components.cookieBanner.savePreferences')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
